import {
  Box,
  Button,
  Group,
  Popover,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineTheme
} from '@mantine/core';
import React, { ReactNode } from 'react';
import { errorBoundaryWrapper } from '@repo/utils';
import { IconHistory, IconPencil } from '@tabler/icons-react';
import dayjs from 'dayjs';

/**
 * Displays a labeled detail field with an accent color and optional icon; when the value has been changed, shows a pencil indicator and a hover popover with change metadata (who changed it, when, and a link to the change log).
 *
 * @param title - Label displayed above the value.
 * @param text - The value content to display.
 * @param isChanged - Whether the field was recently changed, enabling the change indicator and popover.
 * @param isBlurred - Whether to blur the value (e.g. for sensitive data).
 * @param accent - Accent color used for the left bar, icon, and label.
 * @param icon - Optional icon rendered next to the title.
 * @param changeInfo - Metadata about the most recent change, shown in the popover.
 * @param onChangeLogOpen - Callback invoked when the user clicks "View in Change Log".
 */
const DetailsItem = ({
  title,
  text,
  isChanged = false,
  isBlurred = false,
  accent = 'green',
  icon,
  changeInfo,
  onChangeLogOpen,
}: {
  title: string;
  text: any;
  isChanged?: boolean;
  isBlurred?: boolean;
  accent?: string;
  icon?: ReactNode;
  /** Most-recent change metadata — shown in the popover when isChanged is true. */
  changeInfo?: { changedBy: string; changedOn: Date | string; fieldDescription: string };
  /** Called when the user clicks "View in Change Log" inside the popover. */
  onChangeLogOpen?: () => void;
}) => {
  const theme = useMantineTheme();
  const scheme = useComputedColorScheme('light');
  const isDark = scheme === 'dark';

  // Always use the field's own accent color — changed state doesn't alter it
  const accentColor = accent ?? theme.colors.blue[5];

  // Changed background: a tint of the field's own accent color
  const changedBg = isDark
    ? `color-mix(in srgb, ${accentColor} 18%, transparent)`
    : `color-mix(in srgb, ${accentColor} 12%, transparent)`;
  const bg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';

  const content = (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'row',
        borderRadius: 6,
        overflow: 'hidden',
        background: isChanged ? changedBg : bg,
        minHeight: 52,
        width: '100%',
      }}
    >
      {/* Left accent bar */}
      <Box
        style={{
          width: 3,
          flexShrink: 0,
          background: accentColor,
          borderRadius: '6px 0 0 6px',
        }}
      />

      {/* Content */}
      <Box px={10} py={6} style={{ flex: 1, minWidth: 0 }}>
        {/* Label row */}
        <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
          {icon && (
            <Box style={{ color: accentColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {icon}
            </Box>
          )}
          <Text fz={10} fw={600} tt='uppercase' c='dimmed' style={{ letterSpacing: '0.06em', lineHeight: 1 }}>
            {title}
          </Text>
          {isChanged && (
            <Box style={{ marginLeft: 'auto', color: accentColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <IconPencil size={16} />
            </Box>
          )}
        </Box>

        {/* Value */}
        <Text
          fz='sm'
          fw={600}
          style={{
            lineHeight: 1.3,
            wordBreak: 'break-word',
            ...(isBlurred ? { filter: 'blur(5px)', userSelect: 'none' } : {}),
          }}
        >
          {text}
        </Text>
      </Box>
    </Box>
  );

  if (!isChanged || !changeInfo) {
    return content;
  }

  return (
    <Popover width={260} withArrow shadow='md' position='top' trigger='hover'>
      <Popover.Target>
        {/* Native div so Popover hover events are forwarded correctly */}
        <div style={{ display: 'block', width: '100%', cursor: 'pointer' }}>
          {content}
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap='xs'>
          <Group gap='xs' wrap='nowrap'>
            <IconHistory size={16} color={accentColor} style={{ flexShrink: 0 }} />
            <Text size='sm' fw={600}>Field Edited</Text>
          </Group>
          <Text size='xs'>
            <Text span fw={600}>Field: </Text>
            {changeInfo.fieldDescription}
          </Text>
          <Text size='xs'>
            <Text span fw={600}>Changed by: </Text>
            {changeInfo.changedBy}
          </Text>
          <Text size='xs'>
            <Text span fw={600}>Changed on: </Text>
            {dayjs(changeInfo.changedOn).format('MM/DD/YYYY')}
          </Text>
          {onChangeLogOpen && (
            <Button
              size='compact-xs'
              variant='light'
              color='gray'
              leftSection={<IconHistory size={12} />}
              onClick={onChangeLogOpen}
              fullWidth
            >
              View in Change Log
            </Button>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default errorBoundaryWrapper(DetailsItem);
